import qs from 'qs';

import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import TeachersList from '@/components/content-v2/modals/teachers-list';
import ParticipantManagementModal from '@/components/content-v2/modals/participant-management';
import SendForApprovalModal from '@/components/content-v2/modals/send-for-approval-modal';
import CourseApprovalCommentsModal from '@/components/content-v2/templates/common/course-approval-comments';
import AcademicMainTypes from '@/helpers/academicMainType';
import ApprovalStatus from '@/helpers/approvalStatus';
import { validateQueryParameters } from '@/helpers/verifyQueryParameters';
import CommonMixin from '@/mixins/templates/common';
import CourseMixin from '@/mixins/templates/course';
import EqualWebMixin from '@/mixins/equal-web';
import StudentModeMixin from '@/mixins/student-mode';
import { messageContextTypes } from '~/helpers/messageContextTypes';
import { ATooltip } from '@maisaedu/ui-components-a-tooltip';
import ContentSyncMixin from '@/mixins/content-sync';

export default {
  props: {
    showLogo: {
      type    : Boolean,
      default : false
    },
    showTitle: {
      type    : Boolean,
      default : false
    }
  },
  components: {
    TeachersList,
    ParticipantManagementModal,
    SendForApprovalModal,
    CourseApprovalCommentsModal,
    ATooltip
  },
  mixins: [CommonMixin, EqualWebMixin, CourseMixin, StudentModeMixin, ContentSyncMixin],
  data() {
    return {
      ApprovalStatus,
      AcademicMainTypes,
      isTeachersListOpen               : false,
      isGroupsSelfRegisterModalOpen    : false,
      participantManagementModalConfig : {
        isOpen                 : false,
        courseId               : null,
        academicMainId         : null,
        academicMainName       : null,
        academicMainExternalId : null
      },
      isSendForApprovalModalOpen: false
    };
  },
  computed: {
    ...mapGetters({
      getterCourse                   : 'plataforma/newContent/getterCourse',
      getterUserRoleContext          : 'plataforma/user/getterUserContext',
      isImpersonated                 : '_core/auth/isImpersonated',
      getterUnreadNoticesCount       : 'plataforma/noticesBoard/getterUnreadNoticesCount',
      getterInstitutionConfiguration : 'plataforma/institutionConfiguration/getterInstitutionConfiguration',
      getUserId                      : '_core/user/getUserId'
    }),
    ...mapState({
      logoLight   : (state) => state._core.application.logo.light,
      logoDark    : (state) => state._core.application.logo.dark,
      roleAlias   : state => state._core.auth.currentRole.role.alias,
      isLtiAccess : (state) => state.plataforma.user.isLtiAccess
    }),
    isToShowApprovalFlowStatus() {
      if (this.ApprovalStatus.UNDER_CONSTRUCTION !== this.getterCourse.context.approvalFlowStatusId && this.ApprovalStatus.PENDING_CORRECTION !== this.getterCourse.context.approvalFlowStatusId) {
        return this.getterCourse.context.academicMainTypeId === AcademicMainTypes.MODEL && this.getterInstitutionConfiguration.useApprovalFlow;
      }

      return false;
    },
    isToShowSendToApprovalButton() {
      return this.getterCourse.context.academicMainTypeId === this.AcademicMainTypes.MODEL &&
        this.getterInstitutionConfiguration.useApprovalFlow &&
        (this.getterInstitutionConfiguration.useHierarchyApprovalFlow && (this.ApprovalStatus.UNDER_CONSTRUCTION === this.getterCourse.context.approvalFlowStatusId || this.ApprovalStatus.PENDING_CORRECTION === this.getterCourse.context.approvalFlowStatusId));
    },
    isToShowApproveButton() {
      return this.getterCourse.context.academicMainTypeId === this.AcademicMainTypes.MODEL &&
        this.getterInstitutionConfiguration.useApprovalFlow &&
        ((this.ApprovalStatus.UNDER_CONSTRUCTION === this.getterCourse.context.approvalFlowStatusId || this.ApprovalStatus.PENDING_CORRECTION === this.getterCourse.context.approvalFlowStatusId));
    },
    courseTitleTooltipMessage() {
      return `${this.isCommunity ? this.$t("content.layout.timeline.mainTitles.communities") + ": " : ""}${this.getterCourse.context?.academicMainName || ''}`;
    },
    computedStudentOptions() {
      if (!this.getterCourse.context) {
        return [];
      }

      if (this.getterCourse.context.academicMainTypeId === AcademicMainTypes.COMMUNITY) {
        return this.studentOptions.filter(option => !option.hideInCommunity);
      }

      if (this.isLtiAccess) {
        return this.studentOptions.filter(option => !option.hideInLTI);
      }

      return this.studentOptions;
    },
    logo() {
      return (this.$vuetify.theme.dark || this.isDarkContrastMode) ? this.logoDark : this.logoLight;
    },
    isCommunity() {
      return this.getterCourse.context?.academicMainTypeId === AcademicMainTypes.COMMUNITY;
    },
    studentOptions() {
      return [
        {
          text            : 'noticeBoard.notices',
          icon            : 'mdi-bullhorn-outline',
          permissionAlias : 'notices',
          func            : () => this.handleOpenNoticeBoardModal(),
          isHidden        : this.$vuetify.breakpoint.mdAndUp,
          hasBadge        : this.getterUnreadNoticesCount
        },
        {
          text            : 'messages.messages',
          icon            : 'mdi-email-outline',
          permissionAlias : 'messages',
          func            : () => this.handleMessagesClick(),
          isHidden        : this.$vuetify.breakpoint.mdAndUp,
          hasBadge        : this.getterCourse.context.totalUnreadMessages > 0 && !this.isImpersonated
        },
        {
          text : 'content.layout.timeline.course.groups',
          icon : 'mdi-account-group-outline',
          func : () => {
            this.$nuxt.$emit('changeIsOpenModalGroupsSelfRegister', {
              objectName     : this.getterCourse.context && this.getterCourse.context.academicMainName,
              academicMainId : this.getterCourse.context && this.getterCourse.context.academicMainId,
              groupSetId     : null
            });
          },
          hideInCommunity : true,
          hideInLTI       : true,
          isHidden        : this.isMasterCourse || this.isAssociatedCourse
        },
        {
          text            : 'content.layout.timeline.course.gradeGrid',
          icon            : 'mdi-file-table-box-outline',
          func            : () => this.$router.push(`/my-grades/${this.getterCourse.context.academicMainId}`),
          hideInCommunity : true,
          hideInLTI       : false
        },
        {
          text : 'content.classTeachers.label',
          icon : 'mdi-school-outline',
          func : () => {
            this.isTeachersListOpen = true;
          },
          hideInCommunity : false,
          hideInLTI       : true
        }
      ].filter(option => !option.isHidden);
    },
    shouldShowTitle() {
      return this.showTitle && !this.$vuetify.breakpoint.mdAndUp;
    },
    courseOptions() {
      return [
        {
          text            : 'noticeBoard.notices',
          icon            : 'mdi-bullhorn-outline',
          permissionAlias : 'notices',
          func            : () => this.handleOpenNoticeBoardModal(),
          isHidden        : this.$vuetify.breakpoint.mdAndUp
        },
        {
          text            : 'messages.messages',
          icon            : 'mdi-email-outline',
          permissionAlias : 'messages',
          func            : () => this.handleMessagesClick(),
          isHidden        : this.$vuetify.breakpoint.mdAndUp
        },
        {
          text            : 'content.courseOptions.ratingsCenter',
          icon            : 'mdi-clipboard-edit-outline',
          permissionAlias : 'evaluations_center',
          func            : () => {
            this.$nuxt.$emit('changeIsOpenModalEvaluations', {
              showModal              : true,
              contentId              : this.getterCourse.context.contentId,
              academicMainId         : this.getterCourse.context.academicMainId,
              academicMainExternalId : this.getterCourse.context.academicMainExternalId,
              academicMainTypeId     : this.getterCourse.context.academicMainTypeId
            });
          }
        },
        {
          text : 'content.courseOptions.setOfGroups',
          icon : 'mdi-account-supervisor-circle-outline',
          func : () => this.$nuxt.$emit('changeIsOpenModalGroupSetManagement', {
            academicMainId: this.getterCourse.context.academicMainId
          }),
          permissionAlias : 'group_sets',
          isHidden        : this.isMasterCourse || this.isAssociatedCourse
        },
        {
          text            : 'content.courseOptions.gradeBook',
          icon            : 'mdi-file-table-box-outline',
          func            : () => this.$router.push(`/grades/${this.getterCourse.context.academicMainId}`),
          permissionAlias : 'grades_grid'
        },
        {
          text            : 'content.modalRubrics.title',
          icon            : 'mdi-notebook-check-outline',
          permissionAlias : 'rubrics',
          func            : () => {
            this.$nuxt.$emit('changeIsOpenModalRubrics', {
              showModal          : true,
              academicMainId     : this.getterCourse.context.academicMainId,
              academicMainTypeId : this.getterCourse.context.academicMainTypeId
            });
          },
          isHidden: this.isMasterCourse || this.isAssociatedCourse
        },
        {
          text : 'content.courseOptions.participants',
          icon : 'mdi-account-group-outline',
          func : () => {
            this.participantManagementModalConfig = {
              isOpen                 : true,
              courseId               : this.getterCourse.context.academicMainId,
              academicMainId         : this.getterCourse.context.academicMainId,
              academicMainName       : this.getterCourse.context.academicMainName,
              academicMainExternalId : this.getterCourse.context.academicMainExternalId
            };
          }
        },
        {
          text : 'content.courseOptions.importContent',
          icon : 'mdi-import',
          func : () => {
            this.$nuxt.$emit('changeIsOpenImportContentModal', {
              academicMainId     : this.getterCourse.context.academicMainId,
              academicMainTypeId : this.getterCourse.context.academicMainTypeId
            });
          },
          permissionAlias : 'import_content',
          isHidden        : (this.getterCourse && this.getterCourse.topics && this.getterCourse.topics.length) || this.isAssociatedCourse
        },
        {
          text            : 'content.courseOptions.studentMode',
          icon            : 'mdi-monitor-eye',
          permissionAlias : 'student_preview',
          func            : () => this.accessStudentMode(this.getterCourse.context.academicMainId),
          isHidden        : this.$vuetify.breakpoint.mdAndUp || this.isStudentMode
        },
        {
          text : 'content.courseOptions.approvalFlowReopenModelCourse',
          icon : 'mdi-folder-open-outline',
          func : () => {
            this.$dialog.show({
              type              : 'warning',
              title             : this.$t('content.layout.timeline.course.approvalFlow.reopenModal.title'),
              message           : this.$t('content.layout.timeline.course.approvalFlow.reopenModal.message'),
              confirmButtonText : this.$t('content.layout.timeline.course.approvalFlow.reopenModal.confirmButtonText'),
              onConfirm         : ({ startLoading, stopLoading, done }) => {
                const params = {
                  academicMainId : this.$route.params.courseId,
                  data           : { approvalFlowStatusId: ApprovalStatus.UNDER_CONSTRUCTION }
                };
                startLoading();
                this.actionPatchApprovalFlow(params)
                  .then(() => {
                    this.$toast.success(this.$t('content.layout.timeline.course.approvalFlow.reopenModal.successMessage'));
                  }).catch((err) => {
                    this.$toast.error(err.data.error.message);
                  }).finally(() => {
                    done();
                    stopLoading();
                  });
              }
            });
          },
          permissionAlias : 'approval_flow_reopen',
          isHidden        : !this.getterInstitutionConfiguration.useApprovalFlow || this.getterCourse?.context?.academicMainTypeId !== AcademicMainTypes.MODEL || this.getterCourse?.context?.approvalFlowStatusId !== ApprovalStatus.APPROVED
        }
      ].filter(option => !option.isHidden);
    }
  },
  mounted() {
    this.$nuxt.$on('resumeMessageModal', (contexts) => {
      contexts.forEach((c) => {
        if (c.externalId.toString() === this.getterCourse.context.academicMainId.toString()) {
          this.updateContextTotalUnreadMessages({
            academicMainId      : c.externalId,
            totalUnreadMessages : c.totalUnread
          });
        }
      });
    });


    if (this.$route.fullPath.includes('openMessages=true')) {
      this.$nextTick(() => this.handleMessagesClick());
    }

    if (this.$route.fullPath.includes('openNotices=true')) {
      this.$nextTick(() => this.handleOpenNoticeBoardModal());
    }
  },
  methods: {
    ...mapActions({
      actionPatchApprovalFlow: 'plataforma/newContent/actionPatchApprovalFlow'
    }),
    ...mapMutations({
      updateContextTotalUnreadMessages: 'plataforma/newContent/UPDATE_CONTEXT_TOTAL_UNREAD_MESSAGES'
    }),
    goToPreviousPage() {
      const routePlural = AcademicMainTypes.getName(this.getterCourse.context.academicMainTypeId);
      const routeSingular = AcademicMainTypes.getName(this.getterCourse.context.academicMainTypeId, false);

      if (this.getterUserRoleContext.isStudent) {
        const lastFilterUsed = this.getLastFilterUsed();

        const validQuery = validateQueryParameters(lastFilterUsed);

        const queryParams = lastFilterUsed ? `?${validQuery}` : '';

        this.$router.push(`/my-enrollments/${routePlural}${queryParams}`);
      } else if (this.getterUserRoleContext.isAdministrator) {
        this.$router.push(`/academic/${routeSingular}`);
      } else {
        this.$router.push(`/my/${routePlural}`);
      }
    },
    handleMessagesClick() {
      this.$nuxt.$emit('openMessageModal', {
        contexts: [
          { contextType: messageContextTypes.COURSE, externalId: this.getterCourse.context.academicMainId }
        ],
        defaultBoxFilter: this.getterCourse.context.totalUnreadMessages > 0
          ? 'isRead' : 'inbox'
      });
    },
    handleOpenNoticeBoardModal() {
      if (this.getterUserRoleContext.isStudent) {
        this.$nuxt.$emit('openStudentNoticeBoardModal');
      } else {
        this.$nuxt.$emit('openNoticeBoardModal');
      }
    },
    async sendModelToApproval() {
      if (this.getterCourse.context.maxModule > this.getterCourse.context.countModulesWithContent) {
        this.$dialog.show({
          type                 : 'warning',
          title                : this.$t('content.layout.timeline.course.approvalFlow.maxModuleWarningModalTitle'),
          message              : this.$t('content.layout.timeline.course.approvalFlow.maxModuleWarningModalMessage'),
          showCancelButton     : false,
          confirmButtonVariant : 'text'
        });

        return;
      }

      this.isSendForApprovalModalOpen = true;
    },
    confirmSendForApproval() {
      this.$nuxt.$loading.start();

      const params = {
        academicMainId : this.$route.params.courseId,
        data           : { approvalFlowStatusId: ApprovalStatus.AWAITING_APPROVAL }
      };

      this.actionPatchApprovalFlow(params)
        .then(() => {
          this.$toast.success(this.$t('approvalFlow.successMessagesSnackbar.sendWithSuccess'));
        }).catch((err) => {
          this.$toast.error(err.data.error.message);
        }).finally(() => {
          this.isSendForApprovalModalOpen = false;
          this.$nuxt.$loading.finish();
        });
    },
    getLastFilterUsed() {
      const query = sessionStorage.getItem('lastFiltersUsed@' + this.getUserId + this.roleAlias);
      return JSON.parse(query);
    }
  },
  beforeDestroy() {
    this.$nuxt.$off('resumeMessageModal');
  }
};
